import React from 'react';
import { Collapse, Checkbox, Space, Tag, Card } from 'antd';
import { FaShieldAlt } from 'react-icons/fa';
import { Permission } from '@/types/permissions.types';
import { PermissionCategories } from '@/features/settings/constants/permissions';

const { Panel } = Collapse;

interface CustomPermissionsStepProps {
  basePermissions: Permission[];
  customPermissions: Permission[];
  onToggle: (permission: Permission) => void;
}

export const CustomPermissionsStep: React.FC<CustomPermissionsStepProps> = ({
  basePermissions,
  customPermissions,
  onToggle,
}) => {
  const isBasePermission = (permission: Permission): boolean => {
    return basePermissions.includes(permission);
  };

  const isCustomPermission = (permission: Permission): boolean => {
    return customPermissions.includes(permission);
  };

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <FaShieldAlt className="text-gray-600" />
        Custom Permissions
      </h3>

      <p className="text-gray-600 text-sm mb-4">
        Grant additional permissions beyond the user's base role. Only Company Admin can assign
        custom permissions.
      </p>

      <Collapse accordion>
        {PermissionCategories.map((category) => {
          // Filter out permissions that are already in base role
          const availablePermissions = category.permissions.filter(
            (perm) => !isBasePermission(perm.id)
          );

          // Skip category if all permissions are base permissions
          if (availablePermissions.length === 0) {
            return null;
          }

          return (
            <Panel header={category.name} key={category.id}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {availablePermissions.map((perm) => {
                  const isCustom = isCustomPermission(perm.id);

                  return (
                    <Card
                      key={perm.id}
                      size="small"
                      className={isCustom ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}
                    >
                      <Space align="start" style={{ width: '100%' }}>
                        <Checkbox
                          checked={isCustom}
                          onChange={() => onToggle(perm.id)}
                        />
                        <div style={{ flex: 1 }}>
                          <Space>
                            <span className="font-medium text-gray-900">{perm.label}</span>
                            {isCustom && <Tag color="purple">Custom</Tag>}
                          </Space>
                          <div className="text-gray-600 text-xs mt-1">{perm.description}</div>
                        </div>
                      </Space>
                    </Card>
                  );
                })}
              </Space>
            </Panel>
          );
        })}
      </Collapse>
    </div>
  );
};

export default CustomPermissionsStep;
